import {
  FemaAlertScope,
  FemaAlertStatus,
  FemaDisseminationLimits,
  FemaEnvironment,
  FemaEventCodesByChannel,
  FemaWeaHandlingOption,
} from 'types/fema.types';

/**
 * THE ONE IPAWS authoring config (consolidated 2026-08-24 — there were two files with the same
 * export names and different answers; this is the survivor).
 *
 * Every list below is annotated with the RULE and its SOURCE, the same legend as
 * `types/fema.types.ts`: CAP §3.2.x = OASIS CAP 1.2 data dictionary; Profile Table 1/2 = OASIS
 * IPAWS Profile v1.0 CS01; FEMA glossary = FEMA "Event Code Descriptions for Use with IPAWS"
 * (February 2025); FCC §11.31(e) = 47 CFR 11.31(e); RescAlert = our decision (who/when).
 * Local 1:1 copies of all of them live in Fusion-Backend `Docs/issues/planning/fema-ipaws-aosp/`.
 *
 * These lists describe the STANDARDS; they do not represent a COG's permissions. FEMA
 * authorizes each Alerting Authority for its own event codes and dissemination pathways
 * (MULTI-TENANT-MODEL.md), so the tenant's authorized subset is the backend's to answer
 * (`GET /v2/fema/meta`, card BE-08). Until it exists these are the form's data source.
 *
 * Identity is NOT here on purpose: `sender`, `senderName`, `source`, `sent` and the `IPAWSv1.0`
 * code marker are stamped by the backend from the tenant; the frontend never sends them.
 */

/** CAP §3.2.1 msgType `Alert` — the initial message; Update/Cancel/Ack/Error reference it. */
export const CAP_ALERT_MESSAGE_TYPE = 'Alert';
/** Profile Table 1 code: "Value SHALL include the string "IPAWSv1.0" to indicate the Profile version in use." (Backend ensures it.) */
export const IPAWS_PROFILE_CODE = 'IPAWSv1.0';
/** Profile Table 1 eventCode: the `<valueName>` is "SAME" for EAS/CMAS/HazCollect dissemination. */
export const IPAWS_SAME_EVENT_CODE_VALUE_NAME = 'SAME';

/**
 * The Fusion permission keys that gate FEMA alerting (question 4, decided 2026-08-26).
 * Minted in the backend catalog (GetPermissionMapping, fusion_plus); the backend routes
 * enforce them for EVERY access level — an administrator does not bypass them, so the
 * frontend gates must not either (testPermission ANDs level and permission; never OR).
 */
/** `manage` gates Update/Cancel on an existing FEMA message thread (card FE-04, 2026-09-03). */
export const FEMA_PERMISSIONS = {
  create: 'CreateFemaAlert',
  view: 'ViewFemaAlerts',
  manage: 'ManageFemaAlerts',
  /** Gates the client credential page (card FE-11, MULTICOG contract §8). Strict. */
  credentials: 'ManageFemaCredentials',
} as const;
/** The access levels the alert creator admits — the estate's standard list for operator pages. */
export const ALERT_CREATOR_ACCESS_LEVELS = [8, 9, 10, 11, 12, 13];

/** RescAlert: TDL (FEMA's Test Development Lab, COG 120188) is the only environment wired today. */
export const DEFAULT_FEMA_ENVIRONMENT: FemaEnvironment = 'TDL';
/** RescAlert: `Test` until public dissemination is approved (see statusOptions). */
export const DEFAULT_STATUS: FemaAlertStatus = 'Test';
/** CAP §3.2.1 scope `Public` — unrestricted; the common case. */
export const DEFAULT_SCOPE: FemaAlertScope = 'Public';

/**
 * IPAWS dissemination partners from the OASIS IPAWS Profile. WEA was named CMAS when the
 * profile was published, so the operator-facing label is WEA while the CAP channel value stays
 * `CMAS`. Unselected pathways are represented by repeatable `BLOCKCHANNEL` parameters.
 */
export const deliveryChannelOptions = [
  {
    label: 'WEA — Wireless Emergency Alerts',
    shortLabel: 'WEA',
    value: 'CMAS',
    reach:
      'Broadcasts a geographically targeted emergency alert to mobile phones in the affected area.',
    unavailableEventCodes: ['ADR', 'AVA'],
  },
  {
    label: 'EAS — Emergency Alert System',
    shortLabel: 'EAS',
    value: 'EAS',
    reach:
      'Broadcasts an emergency alert through participating radio and television stations serving the affected area.',
    unavailableEventCodes: [],
  },
  {
    label: 'NWEM / NOAA Weather Radio',
    shortLabel: 'NWEM',
    value: 'NWEM',
    reach:
      'Sends a non-weather emergency message through IPAWS to the National Weather Service for dissemination over NOAA Weather Radio All Hazards and other supported NWS systems.',
    unavailableEventCodes: ['RMT', 'RWT'],
  },
] as const;

export type IpawsDeliveryChannel = (typeof deliveryChannelOptions)[number]['value'];

/** The mockup's initial state: Wireless Emergency Alerts selected. */
export const DEFAULT_DELIVERY_CHANNELS: IpawsDeliveryChannel[] = [];

/**
 * IDG Tables 79/80: can this channel carry the event code at all? The list the server serves in
 * `meta/type.dissemination.eventCodesByChannel` (card BE-12) wins. Without it, the local FEMA
 * glossary limits above apply. This is not the COG grant; see `ipawsChannelRules.utils.ts`.
 */
export const isDeliveryChannelAvailableForEvent = (
  channel: (typeof deliveryChannelOptions)[number],
  eventCode: string,
  servedEventCodesByChannel?: FemaEventCodesByChannel | null,
): boolean => {
  const servedEventCodes = servedEventCodesByChannel?.[channel.value];
  if (Array.isArray(servedEventCodes)) return !eventCode || servedEventCodes.includes(eventCode);
  return !(channel.unavailableEventCodes as readonly string[]).includes(eventCode);
};

export const getAvailableDeliveryChannelValues = (
  eventCode: string,
  servedEventCodesByChannel?: FemaEventCodesByChannel | null,
): IpawsDeliveryChannel[] =>
  deliveryChannelOptions
    .filter((channel) =>
      isDeliveryChannelAvailableForEvent(channel, eventCode, servedEventCodesByChannel),
    )
    .map((channel) => channel.value);

/**
 * IDG v4.06: the expiry ceilings per channel — EAS and NWEM 99.5 hours (FEMA 506 and 406), WEA
 * 24 hours (FEMA 613, Table 76 p73). The server serves them in `meta/type.dissemination.limits` (card BE-12);
 * these local numbers apply when it does not.
 */
export const IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK: IpawsChannelExpiryLimits = {
  easExpiresHours: 99.5,
  nwemExpiresHours: 99.5,
  cmasExpiresHours: 24,
};

export type IpawsChannelExpiryLimits = Pick<
  FemaDisseminationLimits,
  'easExpiresHours' | 'nwemExpiresHours' | 'cmasExpiresHours'
>;

export const getDeliveryChannelLabels = (channels?: readonly string[]): string[] => {
  if (!Array.isArray(channels)) return deliveryChannelOptions.map(({ shortLabel }) => shortLabel);
  const selectedChannels = new Set(channels);
  return deliveryChannelOptions
    .filter(({ value }) => selectedChannels.has(value))
    .map(({ shortLabel }) => shortLabel);
};

const allowsValue = <T extends string>(allowed: T[] | undefined, value: string): boolean =>
  !allowed?.length || allowed.includes(value as T);

const WEA_HANDLING_DESCRIPTIONS: Record<string, string> = {
  'Imminent Threat': 'Immediate threat to life or property requiring protective action.',
  'Public Safety': 'Important public-safety information that is not currently life-threatening.',
  AMBER: 'Child abduction emergency.',
  Amber: 'Child abduction emergency.',
  'WEA Test': 'Testing of WEA functionality.',
  Presidential: 'National-level emergency warning.',
  'National / Presidential': 'National-level emergency warning.',
  Earthquake: 'Specialized earthquake WEA handling.',
};

/**
 * The WEA Handling values the COG may use for this draft, from `jurisdictions.weaHandlingOptions`
 * (card BE-12 builds it from IDG Table 86). When the server sends no list, the answer is an empty
 * list, never `WEA Test` (design note finding 1: IDG Table 86 allows `WEA Test` on DMO, RMT and
 * RWT only, so a default `WEA Test` draws FEMA 622 on any other code). The channel field shows
 * the finding "WEA handling options not available" in that case.
 */
export const getAvailableWeaHandlingOptions = (
  authorizedOptions: FemaWeaHandlingOption[] | undefined,
  context: {
    environment?: string;
    eventCode?: string;
    status?: string;
    urgency?: string;
    severity?: string;
    certainty?: string;
  },
): FemaWeaHandlingOption[] => {
  const options = Array.isArray(authorizedOptions) ? authorizedOptions : [];

  return options
    .filter(
      (option) =>
        option?.value &&
        option?.label &&
        allowsValue(option.environments, context.environment ?? '') &&
        allowsValue(option.eventCodes, context.eventCode ?? '') &&
        allowsValue(option.statuses, context.status ?? '') &&
        allowsValue(option.urgencies, context.urgency ?? '') &&
        allowsValue(option.severities, context.severity ?? '') &&
        allowsValue(option.certainties, context.certainty ?? ''),
    )
    .map((option) => ({
      ...option,
      description: option.description || WEA_HANDLING_DESCRIPTIONS[option.value],
    }));
};

/**
 * CAP §3.2.1 status values: Actual, Exercise, System, Test, Draft. Profile: "A value of "Actual"
 * SHALL be used for messages intended for dissemination to the public, including test messages
 * intended for delivery to the public."
 * RescAlert (2026-08-24 review, item 6): Test-only until public dissemination is explicitly
 * approved. `Actual` means a real public warning; it is added back here deliberately, by
 * decision, not by editing a dropdown.
 */
export const statusOptions = [
  { label: 'Test — nonpublic technical testing only', value: 'Test' },
  { label: 'Actual — intended for public dissemination', value: 'Actual' },
];
// list of statuses: Actual for all, Alert, Update Cancel for message type

/**
 * CAP §3.2.1 scope: "Public" — for general dissemination to unrestricted audiences;
 * "Restricted" — to users with a known operational requirement (see <restriction>);
 * "Private" — only to specified addresses (<addresses> REQUIRED).
 */
export const scopeOptions = [
  { label: 'Public — unrestricted distribution', value: 'Public' },
  { label: 'Restricted — operational users only', value: 'Restricted' },
  { label: 'Private — specified recipients only', value: 'Private' },
];

/** CAP §3.2.2 category — REQUIRED, repeatable; the dictionary's twelve values (labels are the dictionary's descriptions). */
export const categoryOptions = [
  {
    label: 'Geophysical',
    value: 'Geo',
    description: 'Geophysical events, including earthquakes, landslides, and volcanoes.',
  },
  {
    label: 'Meteorological',
    value: 'Met',
    description: 'Weather-related events, including floods and severe storms.',
  },
  {
    label: 'General Emergency / Public Safety',
    value: 'Safety',
    description: 'General emergencies and threats to public safety.',
  },
  {
    label: 'Law Enforcement / Security',
    value: 'Security',
    description: 'Law-enforcement, military, homeland, or private-security events.',
  },
  {
    label: 'Rescue / Recovery',
    value: 'Rescue',
    description: 'Rescue, recovery, and missing-person operations.',
  },
  {
    label: 'Fire',
    value: 'Fire',
    description: 'Fire suppression and related rescue events.',
  },
  {
    label: 'Health / Medical',
    value: 'Health',
    description: 'Medical and public-health events.',
  },
  {
    label: 'Environmental',
    value: 'Env',
    description: 'Pollution and other environmental events.',
  },
  {
    label: 'Transportation',
    value: 'Transport',
    description: 'Public and private transportation events.',
  },
  {
    label: 'Infrastructure',
    value: 'Infra',
    description: 'Utility, telecommunications, and other infrastructure events.',
  },
  {
    label: 'CBRNE',
    value: 'CBRNE',
    description: 'Chemical, biological, radiological, nuclear, or explosive threats.',
  },
  {
    label: 'Other',
    value: 'Other',
    description: 'Events that do not fit another CAP category.',
  },
] as const;

const categoryOptionByValue = new Map(categoryOptions.map((option) => [option.value, option]));

export const getCategoryDisplayLabel = (value: string): string => {
  const option = categoryOptionByValue.get(value as (typeof categoryOptions)[number]['value']);
  return option ? `${option.label} — ${option.description}` : value;
};

/**
 * SAME event codes — PROVENANCE (verified 2026-08-24 against the local copies):
 *   Source:  FEMA glossary "Event Code Descriptions for Use with IPAWS", February 2025 —
 *            https://www.fema.gov/sites/default/files/documents/fema_ipaws_event-code-glossary_022025.pdf
 *            local copy: Fusion-Backend Docs/.../FEMA-IPAWS-EVENT-CODE-GLOSSARY-2025-02.md.
 *            This list is EXACTLY that sheet's 22 codes (entered by Jacob Smith, `595faae54`): the
 *            Non-Weather Emergency Message codes "available to State, Local, Territorial, and
 *            Tribal Alerting Authorities". "The weather-related event codes, not listed here, are
 *            reserved for the National Weather Service."
 *   Values:  every code is an authorized EAS event code in FCC §11.31(e) (local copy:
 *            Fusion-Backend Docs/.../FCC-47CFR-11.31-EVENT-CODES.md); labels follow the
 *            regulation's "Nature of Activation" wording.
 *   Rule:    Profile Table 1 eventCode — one and only one SAME three-letter value for EAS/CMAS/
 *            HazCollect; the Backend checks the SHAPE (three letters), never this list, because
 *            "All values for EAS Event Code SHALL be passed through … even if the Event Code is not
 *            shown in FCC Part 11.31, as long as the value is a three-letter code."
 *   Limits:  FEMA glossary — ADR and AVA are EAS & NWEM only (no WEA); RMT and RWT are EAS & WEA
 *            only (no NWEM). The channel-selection cards (FE-06 / BE-12) must honour them.
 *   Owner:   "Use of these event codes by Alerting Authorities depends on established IPAWS
 *            permissions." — the tenant's authorized subset arrives from the backend (`/meta`,
 *            BE-08) and replaces this list; it never grows by hand here.
 */
export const eventCodeOptions = [
  { label: 'Administrative Message (ADR)', value: 'ADR' }, // EAS & NWEM only (FEMA glossary)
  { label: 'Avalanche Watch (AVA)', value: 'AVA' }, // EAS & NWEM only (FEMA glossary)
  { label: 'Avalanche Warning (AVW)', value: 'AVW' },
  { label: 'Blue Alert (BLU)', value: 'BLU' },
  { label: 'Child Abduction Emergency (CAE)', value: 'CAE' },
  { label: 'Civil Danger Warning (CDW)', value: 'CDW' },
  { label: 'Civil Emergency Message (CEM)', value: 'CEM' },
  { label: 'Practice/Demo Warning (DMO)', value: 'DMO' },
  { label: 'Earthquake Warning (EQW)', value: 'EQW' },
  { label: 'Evacuation Immediate (EVI)', value: 'EVI' }, // FEMA's sheet names it "Immediate Evacuation"
  { label: 'Fire Warning (FRW)', value: 'FRW' },
  { label: 'Hazardous Materials Warning (HMW)', value: 'HMW' },
  { label: 'Local Area Emergency (LAE)', value: 'LAE' },
  { label: 'Law Enforcement Warning (LEW)', value: 'LEW' },
  { label: 'Missing and Endangered Persons (MEP)', value: 'MEP' },
  { label: 'Nuclear Power Plant Warning (NUW)', value: 'NUW' },
  { label: 'Radiological Hazard Warning (RHW)', value: 'RHW' },
  { label: 'Required Monthly Test (RMT)', value: 'RMT' }, // EAS & WEA only (FEMA glossary)
  { label: 'Required Weekly Test (RWT)', value: 'RWT' }, // EAS & WEA only (FEMA glossary)
  { label: 'Shelter in Place Warning (SPW)', value: 'SPW' },
  { label: '911 Telephone Outage Emergency (TOE)', value: 'TOE' },
  { label: 'Volcano Warning (VOW)', value: 'VOW' },
];

/** CAP §3.2.2 urgency — REQUIRED: Immediate, Expected, Future, Past, Unknown. */
export const urgencyOptions = [
  { label: 'Immediate', value: 'Immediate' },
  { label: 'Expected', value: 'Expected' },
  { label: 'Future', value: 'Future' },
  { label: 'Past', value: 'Past' },
  { label: 'Unknown', value: 'Unknown' },
];

/** CAP §3.2.2 severity — REQUIRED: Extreme, Severe, Moderate, Minor, Unknown. */
export const severityOptions = [
  { label: 'Extreme', value: 'Extreme' },
  { label: 'Severe', value: 'Severe' },
  { label: 'Moderate', value: 'Moderate' },
  { label: 'Minor', value: 'Minor' },
  { label: 'Unknown', value: 'Unknown' },
];

/** CAP §3.2.2 certainty — REQUIRED: Observed, Likely, Possible, Unlikely, Unknown (the deprecated "Very Likely" is never offered). */
export const certaintyOptions = [
  { label: 'Observed', value: 'Observed' },
  { label: 'Likely', value: 'Likely' },
  { label: 'Possible', value: 'Possible' },
  { label: 'Unlikely', value: 'Unlikely' },
  { label: 'Unknown', value: 'Unknown' },
];

/** FEMA WEA profile: priority values accepted when the CMAS/WEA pathway is enabled. */
export const WEA_PRIORITY_VALUES = {
  urgency: ['Immediate', 'Expected'],
  severity: ['Extreme', 'Severe'],
  certainty: ['Observed', 'Likely'],
} as const;

/** CAP §3.2.2 responseType — optional, repeatable; the dictionary's nine values. */
export const responseTypeOptions = [
  { label: 'Shelter', value: 'Shelter' },
  { label: 'Evacuate', value: 'Evacuate' },
  { label: 'Prepare', value: 'Prepare' },
  { label: 'Execute', value: 'Execute' },
  { label: 'Avoid', value: 'Avoid' },
  { label: 'Monitor', value: 'Monitor' },
  { label: 'Assess — not recommended for public warnings', value: 'Assess' },
  { label: 'All Clear', value: 'AllClear' },
  { label: 'None', value: 'None' },
];

/** Authoring-only choice. It is omitted because CAP represents this by excluding responseType. */
export const NOT_SPECIFIED_RESPONSE_TYPE = 'NotSpecified';
export const notSpecifiedResponseTypeOption = {
  label: 'Not Specified',
  value: NOT_SPECIFIED_RESPONSE_TYPE,
};

/** FEMA WEA profile: response types accepted when the CMAS/WEA pathway is enabled. */
export const WEA_RESPONSE_TYPE_VALUES = [
  'Shelter',
  'Evacuate',
  'Prepare',
  'Execute',
  'Avoid',
  'Monitor',
  NOT_SPECIFIED_RESPONSE_TYPE,
] as const;

/** CAP §3.2.2: `Assess` "SHOULD NOT be used in public warning applications" — the form uses this list. */
export const publicWarningResponseTypeOptions = responseTypeOptions.filter(
  (option) => option.value !== 'Assess',
);
